/**
 * Match Grouping Utilities
 * 
 * Groups matches by date for organized display on the landing page.
 */

/**
 * Format a date as a readable group label
 * @param {Date} date - The date to format
 * @returns {string} - "Today", "Yesterday", or "Mon, Oct 11"
 */
export function formatGroupLabel(date) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  // Reset time to midnight for accurate comparison
  const resetTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dateOnly = resetTime(date);
  const todayOnly = resetTime(today);
  const yesterdayOnly = resetTime(yesterday);
  
  if (dateOnly.getTime() === todayOnly.getTime()) {
    return 'Today';
  }
  
  if (dateOnly.getTime() === yesterdayOnly.getTime()) {
    return 'Yesterday';
  }
  
  // Format as "Mon, Oct 11"
  const options = { weekday: 'short', month: 'short', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
}

/**
 * Get a sortable date key from a timestamp
 * @param {string|number} timestamp - Firebase timestamp or ISO string
 * @returns {string} - "2025-10-11" format for sorting
 */
export function getDateKey(timestamp) {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Group matches by date
 * @param {Array} matches - Array of match objects
 * @returns {Array} - Array of { date, label, matches } objects, sorted newest first
 */
export function groupMatchesByDate(matches) {
  if (!matches || matches.length === 0) {
    return [];
  }
  
  // Group matches by date key
  const groups = {};
  
  matches.forEach(match => {
    // Use createdAt timestamp (fallback to current time if missing)
    const timestamp = match.createdAt || Date.now();
    const dateKey = getDateKey(timestamp);
    
    if (!groups[dateKey]) {
      groups[dateKey] = {
        dateKey,
        date: new Date(timestamp),
        matches: []
      };
    }
    
    groups[dateKey].matches.push(match);
  });
  
  // Convert to array and sort by date (newest first)
  const groupArray = Object.values(groups).sort((a, b) => {
    return b.date.getTime() - a.date.getTime();
  });
  
  // Add formatted labels
  groupArray.forEach(group => {
    group.label = formatGroupLabel(group.date);
  });
  
  return groupArray;
}

/**
 * Get total count of matches across all groups
 * @param {Array} groups - Array of group objects
 * @returns {number} - Total match count
 */
export function getTotalMatchCount(groups) {
  return groups.reduce((total, group) => total + group.matches.length, 0);
}

